
package com.mps.asc_app.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp

private const val ASC_MEMBERSHIP_PDF_URL = "/forms/aufnahme_antrag_2026.pdf"
private const val PADEL_MEMBERSHIP_PDF_URL = "/forms/beitritt_padel.pdf"

@Composable
fun Blockscreen(
    modifier: Modifier = Modifier,
    membershipRequestSubmitted: Boolean = false,
    forceAscMembershipFormOnly: Boolean = false,
    forcePadelMembershipFormOnly: Boolean = false,
    onRefresh: (() -> Unit)? = null,
    onLogout: (() -> Unit)? = null,
    onOpenAscMembershipForm: (() -> Unit)? = null,
    onOpenPadelMembershipForm: (() -> Unit)? = null,
) {
    val uriHandler = LocalUriHandler.current

    Column(
            modifier = modifier
                .fillMaxSize()
            .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp, Alignment.CenterVertically),
            horizontalAlignment = Alignment.CenterHorizontally
    ) {
            Text(
                        text = "Vielen Dank für deine Registrierung beim ASC Padel.",
                style = MaterialTheme.typography.headlineSmall,
            textAlign = TextAlign.Center
        )

        Spacer(Modifier.height(12.dp))

        if (membershipRequestSubmitted) {
            Text(
                text = "Dein Mitgliedsantrag ist bei uns eingegangen und wird derzeit bearbeitet. Sobald die Bearbeitung abgeschlossen ist, informieren wir dich umgehend. Im Anschluss wird dein Zugang freigeschaltet und du kannst Plätze buchen.",
                style = MaterialTheme.typography.bodyMedium,
                textAlign = TextAlign.Center
            )
        } else if (forceAscMembershipFormOnly) {
            Text(
                text = "Wir haben deine Daten geprüft: Du bist noch kein Mitglied beim ASC um bei uns mitglied zu werden reiche bitte das Mitgliederformular hier unten ein: dann PDF oder fülle das OnlineFormular aus:",
                style = MaterialTheme.typography.bodyMedium,
                textAlign = TextAlign.Center
            )

            MembershipActionCard(
                title = "ASC-Mitgliedsantrag",
                description = "Fülle das Formular aus oder lade das PDF herunter, um deine Mitgliedschaft zu beantragen.",
                onOpenOnline = { onOpenAscMembershipForm?.invoke() },
                onOpenPdf = { uriHandler.openUri(ASC_MEMBERSHIP_PDF_URL) },
                onlineEnabled = onOpenAscMembershipForm != null,
            )
        } else if (forcePadelMembershipFormOnly) {
            Text(
                text = "Wir haben deine Daten geprüft: Du bist bereits Mitglied beim ASC, aber noch keiner Sparte (Tennis/Padel) zugeordnet. Bitte reiche das Padel-Beitrittsformular hier unten ein: dann PDF oder fülle das Online-Formular aus:",
                style = MaterialTheme.typography.bodyMedium,
                textAlign = TextAlign.Center
            )

            MembershipActionCard(
                title = "Padel-Beitrittsformular",
                description = "Fülle das Formular aus oder lade das PDF herunter, um der Padel-Sparte beizutreten.",
                onOpenOnline = { onOpenPadelMembershipForm?.invoke() },
                onOpenPdf = { uriHandler.openUri(PADEL_MEMBERSHIP_PDF_URL) },
                onlineEnabled = onOpenPadelMembershipForm != null,
            )
        } else {
            Text(
                text = "Deine Registrierung ist bei uns eingegangen. Derzeit prüfen wir, ob bereits eine ASC-Mitgliedschaft besteht und ob eine Zuordnung zur Tennis- oder Padel-Sparte vorliegt. Sollte eine Zuordnung fehlen, erhältst du im Anschluss eine E-Mail von uns.",
                style = MaterialTheme.typography.bodyMedium,
                textAlign = TextAlign.Center
            )

            MembershipActionCard(
                title = "Noch kein ASC-Mitglied?",
                description = "Wenn du die Bearbeitung beschleunigen möchtest, kannst du hier den Antrag für die ASC-Mitgliedschaft inklusive Tennis/Padel stellen.",
                onOpenOnline = { onOpenAscMembershipForm?.invoke() },
                onOpenPdf = { uriHandler.openUri(ASC_MEMBERSHIP_PDF_URL) },
                onlineEnabled = onOpenAscMembershipForm != null,
            )

            MembershipActionCard(
                title = "Schon ASC-Mitglied, aber noch nicht in der Padel-Sparte?",
                description = "In diesem Fall kannst du hier für 2 € pro Monat der Padel-Sparte beitreten.",
                onOpenOnline = { onOpenPadelMembershipForm?.invoke() },
                onOpenPdf = { uriHandler.openUri(PADEL_MEMBERSHIP_PDF_URL) },
                onlineEnabled = onOpenPadelMembershipForm != null,
            )
        }

        if (onRefresh != null || onLogout != null) {
            Spacer(Modifier.height(8.dp))
        }

        if (onRefresh != null) {
            Button(onClick = onRefresh) {
                Text("Aktualisieren")
            }
        }

        if (onRefresh != null && onLogout != null) {
            Spacer(Modifier.height(12.dp))
        }

        if (onLogout != null) {
            Button(onClick = onLogout) {
                Text("Abmelden")
            }
        }
    }
}

@Composable
private fun MembershipActionCard(
    title: String,
    description: String,
    onOpenOnline: () -> Unit,
    onOpenPdf: () -> Unit,
    onlineEnabled: Boolean,
) {
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(text = title, style = MaterialTheme.typography.titleSmall)
            Text(
                text = description,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Button(
                    onClick = onOpenOnline,
                    enabled = onlineEnabled,
                    modifier = Modifier.weight(1f)
                ) {
                    Text("Online-Formular")
                }
                OutlinedButton(onClick = onOpenPdf, modifier = Modifier.weight(1f)) {
                    Text("PDF öffnen")
                }
            }
        }
    }
}
